import {
  OrdersAdditionalOrderData,
  OrdersDtoOrderDto,
  OrdersOpenOrderAmounts,
  SvcGetOrganizationUnitSummary,
} from "@springtree/eva-services-core";
import { GetDigitalGiftCardsForOrderResponse } from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import { useTranslations } from "next-intl";

import IconCashier from "@/icons/icon-cashier";
import IconGift from "@/icons/icon-gift";
import IconGiftCard from "@/icons/icon-gift-card";
import IconPickUp from "@/icons/icon-pick-up";
import IconShipping from "@/icons/icon-shipping";
import IconStoreLocator from "@/icons/icon-store-locator";
import IconWallet from "@/icons/icon-wallet";
import { useConfiguration } from "~/contexts/configuration";
import { useEvaService } from "~/hooks/use-eva-service";
import useGiftcard from "~/hooks/use-giftcard";
import Constants from "~/utils/constants";
import evaUtils from "~/utils/eva-utils";
import intlUtils from "~/utils/intl-utils";

import FormattedAddress from "./formatted-address";
import Icon from "./icon";
import styles from "./order-info.module.scss";

type Props = {
  order: OrdersDtoOrderDto;
  amounts?: OrdersOpenOrderAmounts;
  additionalOrderData?: OrdersAdditionalOrderData;
  className?: string;
  cardClassName?: string;
  cardHeaderClassName?: string;
  cardContentClassName?: string;
  digitalGiftcards?: GetDigitalGiftCardsForOrderResponse["Result"];
};

export default function OrderInfo({
  order,
  amounts,
  additionalOrderData,
  className,
  cardClassName,
  cardContentClassName,
  cardHeaderClassName,
  digitalGiftcards,
}: Props) {
  const t = useTranslations();
  const priceFormatter = intlUtils.usePriceFormatter();
  const { configuration } = useConfiguration();

  const getOrganizationUnitSummary = useEvaService(SvcGetOrganizationUnitSummary);

  const products = order.Lines?.filter((el) => el.ProductID && !el.IsCancelled);
  const firstShippingMethod = products?.find((line) => line.ShippingMethod && line.ShippingMethod.Name);
  const digitalGiftCards = products?.filter((el) => el.IsDigitalGiftCard);

  const hasPudo = evaUtils.getCustomFieldValueForResource(Constants.PUDO_ID, order, configuration) as string;

  const storeInfo = useQuery({
    queryKey: ["getStoreInfo", order.PickupOrganizationUnitID],
    staleTime: Infinity,
    queryFn: async () => {
      const data = await getOrganizationUnitSummary({
        OrganizationUnitID: order.PickupOrganizationUnitID,
      });

      if (!data) {
        return null;
      }

      return data;
    },
    enabled: !!order.PickupOrganizationUnitID && order.PickupOrganizationUnitID !== order.OriginatingOrganizationUnitID,
  });

  const digitalGiftCardsQuery = useGiftcard().prepareGetDigitalGiftcardOptionsQuery(
    digitalGiftcards ? void 0 : order || void 0
  );

  // Display the shipping address for delivery orders or pickup orders (with physical products)
  const showsShippingAddressCard =
    (!order.HasDelivery || order.ShippingAddress) && digitalGiftCards?.length !== products?.length;

  return (
    <div className={className}>
      {/*  show if not cancelled - shipping address (if present), shipping method (if present) and gift wrapping (if present) */}
      {order.TotalItems != 0 && (
        <div className={classNames(styles.card, cardClassName)}>
          <h2 className={cardHeaderClassName}>{t("generic.your_shipping_info")}</h2>
          <div className={classNames(styles.cardItems, cardContentClassName)}>
            {showsShippingAddressCard && (
              <div className={styles.cardItem}>
                <div className={styles.cardItemHeader}>
                  {order.HasDelivery ? (
                    hasPudo ? (
                      <>
                        <Icon icon={IconPickUp} />
                        <h3>{t("generic.checkout.pickUpPoint")}</h3>
                      </>
                    ) : (
                      <>
                        <Icon icon={IconStoreLocator} />
                        <h3>{t("generic.shipping_address")}</h3>
                      </>
                    )
                  ) : (
                    <>
                      <Icon icon={IconPickUp} />
                      <h3>
                        {t(
                          order.SoldFromOrganizationUnitID == order.PickupOrganizationUnitID
                            ? "generic.purchasedAt"
                            : "generic.checkout.collectInStore"
                        )}
                      </h3>
                    </>
                  )}
                </div>
                <FormattedAddress address={order.HasDelivery ? order.ShippingAddress : storeInfo.data?.Address!} />
              </div>
            )}
            {digitalGiftCards && digitalGiftCards.length > 0 && (
              <>
                {digitalGiftCards.length != products?.length && <div className={styles.divider} aria-hidden />}
                <div className={styles.cardItem}>
                  <div className={styles.cardItemHeader}>
                    <Icon icon={IconGiftCard} />
                    <h3>{t("generic.shipping_method_giftcard")}</h3>
                  </div>
                  {((digitalGiftcards as any) ?? digitalGiftCardsQuery?.data)?.map((card: any, i: number) => (
                    <p key={i}>{card.Data?.ToEmailAddress}</p>
                  ))}
                </div>
              </>
            )}
            {firstShippingMethod && (
              <>
                <div className={styles.divider} aria-hidden />
                <div className={styles.cardItem}>
                  <div className={styles.cardItemHeader}>
                    <Icon icon={IconShipping} />
                    <h3>{t("generic.shipping_method")}</h3>
                  </div>
                  {firstShippingMethod.ShippingMethod!.Carrier?.Name} {firstShippingMethod.ShippingMethod!.Name}
                </div>
              </>
            )}
            {additionalOrderData?.GiftWrappingOptions?.WrapOrder && (
              <>
                <div className={styles.divider} aria-hidden />
                <div className={styles.cardItem}>
                  <div className={styles.cardItemHeader}>
                    <Icon icon={IconGift} />
                    <h3>{t("generic.gift_option.title")}</h3>
                  </div>
                  <p>
                    {t.rich("generic.thankyoupage.giftOption.standard", {
                      price: (chunks) => (
                        <span>
                          {priceFormatter.currency(amounts?.Types?.GiftWrappingCosts.InTax || 0, {
                            currency: order.CurrencyID,
                          })}
                        </span>
                      ),
                    })}
                  </p>
                  {additionalOrderData?.GiftWrappingOptions?.Message && (
                    <p>
                      {t("generic.thankyoupage.gift_option.your_message")}
                      <br />
                      {additionalOrderData?.GiftWrappingOptions?.Message}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* billing e payment */}
      <div className={classNames(styles.card, cardClassName)}>
        <h2 className={cardHeaderClassName}>{t("generic.thankyoupage.gift_option.your_billing_payment")}</h2>
        <div className={classNames(styles.cardItems, cardContentClassName)}>
          <div className={styles.cardItem}>
            <div className={styles.cardItemHeader}>
              <Icon icon={IconStoreLocator} />
              <h3>{t("generic.billing_address")}</h3>
            </div>
            <FormattedAddress address={order.BillingAddress} fiscalId={order.FiscalID} />
          </div>

          <div className={styles.divider} aria-hidden />
          <div className={styles.cardItem}>
            <div className={styles.cardItemHeader}>
              <Icon icon={order.HasDelivery ? IconWallet : IconCashier} />
              <h3>{t("generic.payment_method")}</h3>
            </div>
            {order.Payments?.length === 0 ? (
              order.HasDelivery ? (
                <p>{t("generic.cashOnDelivery")}</p>
              ) : (
                <p>{t("generic.payInStore")}</p>
              )
            ) : (
              order.Payments?.map((paymentMethod, index) => (
                <p key={index} className={styles.paymentLabel}>
                  {order.Payments && order.Payments.length > 1
                    ? t("generic.paidWith", {
                        amount: priceFormatter.currency(paymentMethod.PaidAmount, { currency: order.CurrencyID }),
                        method: paymentMethod.PaymentName ?? paymentMethod.Description,
                      })
                    : paymentMethod.PaymentName ?? paymentMethod.Description}
                </p>
              ))
            )}
          </div>
        </div>
      </div>
      {/* fine blocco comune */}
    </div>
  );
}
